/** Sign-in pages never continue back into themselves. */
const authPages = ['/login', '/signup', '/auth/magic-link', '/oauth/callback']

/** A continuation is an app page, never an external URL or a sign-in loop. */
export function safeReturnTo(value: string | null | undefined, fallback = '/app') {
  if (!value || !value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u0020]/.test(value) || value.length > 2048) return fallback
  try {
    const path = decodeURIComponent(new URL(value, 'https://app.streambridge.live').pathname).replace(/\/+$/, '').toLowerCase()
    if (authPages.includes(path)) return fallback
  } catch {
    return fallback
  }
  return value
}

export function authLink(route: '/login' | '/signup', returnTo: string) {
  return `${route}?${new URLSearchParams({ return_to: safeReturnTo(returnTo) })}`
}

/** Pages whose fragment carries a credential: an invite link is `/invite#<token>`. Coming back to them drops it (the
 *  invite page keeps the token itself, pendingInvite). */
const fragmentSecretPages = ['/invite']

/** The sign-in page, coming back to the page at `location` (its path, search and hash) afterwards: the site's Log in
 *  and Sign in links. From the home page and the sign-in pages it lands on Events (`/app`), where hosts start. */
export function logInLink({ pathname, search, hash }: { pathname: string; search: string; hash: string }) {
  const path = pathname.replace(/(.)\/$/, '$1')
  if (path === '/' || authPages.includes(path)) return authLink('/login', '/app')
  return authLink('/login', `${pathname}${search}${fragmentSecretPages.includes(path) ? '' : hash}`)
}

/**
 * Starts Google or Strava sign-in on the API. `mode: 'viewer'` (the watch screen) makes a new account a viewer, with
 * no organisation; without it a new account is a producer's.
 */
export function oauthStartUrl(provider: 'google' | 'strava', returnTo: string, mode?: 'viewer') {
  const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'
  const params = new URLSearchParams({ return_to: safeReturnTo(returnTo) })
  if (mode) params.set('mode', mode)
  return `${base}/auth/${provider}/start?${params}`
}
